/**
 * Typed SSE events for the chat turn engine (HIG-5970/HIG-5971, design:
 * docs/research/chat-engine-design-no-effect.md §2).
 *
 * Pure TS — no React imports. Each event the turn pipeline folds has a zod
 * schema; schemas validate only the fields the accumulator reads and pass the
 * rest through, so additive backend changes don't break decoding. A frame that
 * fails JSON.parse, has no string `type`, or matches a handled type with a
 * malformed body yields a typed `SseDecodeError` (never a silent skip). Frames
 * with types the pipeline doesn't handle decode to `null` — ignored, matching
 * the old switch's default branch.
 */
import { Result, err, ok } from 'neverthrow'
import { z } from 'zod'
import type { ToolCall } from '../types'
import { decodeTurnFrame, sseDecodeError, type SseDecodeError, type TurnErrorContext } from './errors'

// Typed but unvalidated pass-throughs: the fold forwards these to the store
// verbatim (same trust as the pre-engine hook), so decoding must not reject
// shape variants the backend evolves.
const toolCallMetadataSchema = z.custom<ToolCall['metadata']>()
const toolCallStepsSchema = z.custom<ToolCall['steps']>()
const toolCallViewSchema = z.custom<ToolCall['view']>()

// Subagent-scoped events reuse the top-level event types with these two extra
// fields; the accumulator folds them into the parent tool call's `subagents`.
const subagentScope = {
  parentToolCallId: z.string().optional(),
  subagentIndex: z.number().optional(),
}

const metadataEventSchema = z
  .object({
    type: z.literal('metadata'),
    // Optional because subagent-scoped metadata omits it; top-level metadata
    // always carries it and the fold falls back to the turn's chat id.
    conversationId: z.string().optional(),
    messageId: z.string().optional(),
    lastUserMessageId: z.string().optional(),
    model: z.string().optional(),
    llmProvider: z.string().optional(),
    ...subagentScope,
  })
  .passthrough()

const reasoningEventSchema = z
  .object({
    type: z.literal('reasoning'),
    messageId: z.string(),
    content: z.string().optional(),
    ...subagentScope,
  })
  .passthrough()

const textEventSchema = z
  .object({
    type: z.literal('text'),
    messageId: z.string(),
    content: z.string(),
    ...subagentScope,
  })
  .passthrough()

const toolUseEventSchema = z
  .object({
    type: z.literal('toolUse'),
    messageId: z.string(),
    toolId: z.string(),
    name: z.string(),
    input: z.unknown().optional(),
    willSendToolResponse: z.boolean().optional(),
    metadata: toolCallMetadataSchema.optional(),
    view: toolCallViewSchema.optional(),
    ...subagentScope,
  })
  .passthrough()

const toolUpdateEventSchema = z
  .object({
    type: z.literal('toolUpdate'),
    messageId: z.string(),
    toolId: z.string(),
    input: z.unknown().optional(),
    metadata: toolCallMetadataSchema.optional(),
    steps: toolCallStepsSchema.optional(),
    view: toolCallViewSchema.optional(),
    ...subagentScope,
  })
  .passthrough()

const toolResponseEventSchema = z
  .object({
    type: z.literal('toolResponse'),
    messageId: z.string(),
    toolId: z.string(),
    name: z.string().optional(),
    content: z.string(),
    metadata: toolCallMetadataSchema.optional(),
    steps: toolCallStepsSchema.optional(),
    view: toolCallViewSchema.optional(),
    ...subagentScope,
  })
  .passthrough()

const loadingEventSchema = z
  .object({
    type: z.literal('loading'),
    messageId: z.string(),
    toolId: z.string().optional(),
    toolName: z.string().optional(),
    loaded: z.boolean().optional(),
    view: toolCallViewSchema.optional(),
    ...subagentScope,
  })
  .passthrough()

const queuedEventSchema = z
  .object({
    type: z.literal('queued'),
    queuedId: z.string(),
  })
  .passthrough()

const ragChunkSchema = z
  .object({
    score: z.number(),
    documentName: z.string(),
    text: z.string().optional(),
  })
  .passthrough()

const ragUsedEventSchema = z
  .object({
    type: z.literal('rag-used'),
    chunks: z.array(ragChunkSchema).optional(),
  })
  .passthrough()

const ragCheckedEventSchema = z
  .object({
    type: z.literal('rag-checked'),
  })
  .passthrough()

const doneEventSchema = z
  .object({
    type: z.literal('done'),
    succeeded: z.boolean().optional(),
    // Subagent-scoped `done` events fire while the parent turn still streams;
    // only a top-level `done` (no parentToolCallId) marks the turn terminal.
    ...subagentScope,
  })
  .passthrough()

/** A conversation's running credit spend and context fill; also the `GET /conversations/:id/usage` body. */
export const chatUsageSchema = z.object({
  credits: z.number(),
  contextTokens: z.number(),
  contextWindow: z.number().nullable(),
  turnContextTokens: z.array(z.number()),
  // Older backends omit these: an undated snapshot that covers the whole chat.
  /** Epoch ms of the last recorded step; decides which of two snapshots is fresher. */
  updatedAt: z.number().nullable().default(null),
  /** Epoch ms of the first recorded step. */
  trackedSince: z.number().nullable().default(null),
  /** False when the chat had turns before tracking began, so `credits` misses the older spend. */
  complete: z.boolean().default(true),
})

export type ChatUsage = z.infer<typeof chatUsageSchema>

const usageEventSchema = z
  .object({
    type: z.literal('usage'),
    conversationId: z.string().optional(),
    // Absent from backends that predate per-chat totals; the fold then has nothing to publish.
    chatUsage: chatUsageSchema.optional(),
    ...subagentScope,
  })
  .passthrough()

const errorEventSchema = z
  .object({
    type: z.literal('error'),
    error: z.string().optional(),
  })
  .passthrough()

const chatSseEventSchema = z.discriminatedUnion('type', [
  metadataEventSchema,
  reasoningEventSchema,
  textEventSchema,
  toolUseEventSchema,
  toolUpdateEventSchema,
  toolResponseEventSchema,
  loadingEventSchema,
  queuedEventSchema,
  ragUsedEventSchema,
  ragCheckedEventSchema,
  usageEventSchema,
  doneEventSchema,
  errorEventSchema,
])

export type ChatSSEMetadataEvent = z.infer<typeof metadataEventSchema>
export type ChatSSEReasoningEvent = z.infer<typeof reasoningEventSchema>
export type ChatSSETextEvent = z.infer<typeof textEventSchema>
export type ChatSSEToolUseEvent = z.infer<typeof toolUseEventSchema>
export type ChatSSEToolUpdateEvent = z.infer<typeof toolUpdateEventSchema>
export type ChatSSEToolResponseEvent = z.infer<typeof toolResponseEventSchema>
export type ChatSSELoadingEvent = z.infer<typeof loadingEventSchema>
export type ChatSSEQueuedEvent = z.infer<typeof queuedEventSchema>
export type ChatSSERagUsedEvent = z.infer<typeof ragUsedEventSchema>
export type ChatSSERagChunk = z.infer<typeof ragChunkSchema>
export type ChatSSERagCheckedEvent = z.infer<typeof ragCheckedEventSchema>
export type ChatSSEUsageEvent = z.infer<typeof usageEventSchema>
export type ChatSSEDoneEvent = z.infer<typeof doneEventSchema>
export type ChatSSEErrorEvent = z.infer<typeof errorEventSchema>

export type ChatSSEEvent = z.infer<typeof chatSseEventSchema>

/**
 * Events the accumulator folds — everything except the stream-level control
 * events (`error` fails the turn, `queued`/`done` are pipeline lifecycle).
 */
export type ChatSSEContentEvent = Exclude<ChatSSEEvent, ChatSSEErrorEvent | ChatSSEQueuedEvent | ChatSSEDoneEvent>

/** An event carrying both subagent scope fields, with them narrowed to present. */
export type SubagentScopedEvent = ChatSSEEvent & { parentToolCallId: string; subagentIndex: number }

/** An event addressed to a subagent's stream rather than the parent turn. */
export function isSubagentScoped(event: ChatSSEEvent): event is SubagentScopedEvent {
  return (
    'parentToolCallId' in event &&
    typeof event.parentToolCallId === 'string' &&
    'subagentIndex' in event &&
    typeof event.subagentIndex === 'number'
  )
}

const HANDLED_EVENT_TYPES: ReadonlySet<string> = new Set(
  chatSseEventSchema.options.map((option) => option.shape.type.value),
)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Decode one SSE frame into a typed event. `null` on the Ok side means the
 * frame is valid but carries an event type the turn pipeline doesn't handle
 * (future additions) — the caller drops it silently.
 */
export function decodeTurnEvent(ctx: TurnErrorContext, rawFrame: string): Result<ChatSSEEvent | null, SseDecodeError> {
  return decodeTurnFrame(ctx, rawFrame).andThen((json) => {
    if (!isRecord(json) || typeof json.type !== 'string') {
      return err(sseDecodeError(ctx, rawFrame))
    }
    if (!HANDLED_EVENT_TYPES.has(json.type)) {
      return ok(null)
    }
    const parsed = chatSseEventSchema.safeParse(json)
    return parsed.success ? ok(parsed.data) : err(sseDecodeError(ctx, rawFrame))
  })
}
